import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Search, Loader2, Send, Clock, CheckCircle, XCircle,
  FileText, RefreshCw, Eye,
  MessageSquare, Calendar, Globe,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import DesignerLayout from './DesignerLayout';
import TemplateThumbnail from '../../components/shared/TemplateThumbnail';
import LetterTile from '../../components/shared/LetterTile';

// Submission rows expose the same `canvas_size`-style legacy fields the
// admin/public sides use; normalize them into the shape TemplateThumbnail expects.
const _adaptSubmission = (d) => ({
  ...d,
  canvas_width:  d.canvas_width  || d.canvas_size?.width  || 1080,
  canvas_height: d.canvas_height || d.canvas_size?.height || 1080,
});

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending:   { label: 'Pending Review', badge: 'bg-amber-100 text-amber-700',    border: 'border-amber-200',   bg: 'bg-amber-50/50',   icon: Clock },
  approved:  { label: 'Approved',       badge: 'bg-emerald-100 text-emerald-700', border: 'border-emerald-200', bg: 'bg-emerald-50/50', icon: CheckCircle },
  rejected:  { label: 'Rejected',       badge: 'bg-red-100 text-red-600',        border: 'border-red-200',     bg: 'bg-red-50/50',     icon: XCircle },
  published: { label: 'Published',      badge: 'bg-blue-100 text-blue-700',      border: 'border-blue-200',    bg: 'bg-blue-50/50',    icon: Globe },
};

const FILTER_TABS = [
  { key: 'all',       label: 'All Submissions' },
  { key: 'pending',   label: 'Pending' },
  { key: 'approved',  label: 'Approved' },
  { key: 'rejected',  label: 'Rejected' },
  { key: 'published', label: 'Published' },
];

const DesignerSubmissions = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [detailDesign, setDetailDesign] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchDesigns = () => {
    const token = localStorage.getItem('user_token');
    if (!token) { setLoading(false); return; }
    if (!API_URL) { setLoading(false); return; }
    setLoading(true);
    fetch(`${API_URL}/api/designer/templates`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        const arr = Array.isArray(data) ? data : [];
        setDesigns(arr.sort((a, b) =>
          new Date(b.submitted_at || b.created_at) - new Date(a.submitted_at || a.created_at)
        ));
      })
      .catch(() => toast({ title: 'Error', description: 'Failed to load submissions', variant: 'destructive' }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchDesigns(); }, []);

  const handleResubmit = async (design) => {
    setSubmitting(true);
    const token = localStorage.getItem('user_token');
    try {
      const res = await fetch(`${API_URL}/api/designer/templates/${design.id}/resubmit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        toast({ title: 'Resubmitted!', description: 'Your template is pending review again.' });
        setDetailDesign(null);
        fetchDesigns();
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail || 'Resubmission failed', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Error', description: 'Network error', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  // Normalize: designer_templates uses `title` and `status`; legacy uses `name` and `submission_status`
  const normStatus = (d) => d?.submission_status || d?.status;
  const normName = (d) => d?.title || d?.name || 'Untitled';

  const filtered = designs.filter(Boolean).filter(d => {
    const q = search.toLowerCase();
    const matchSearch = normName(d)?.toLowerCase().includes(q);
    const matchTab = activeTab === 'all' || normStatus(d) === activeTab;
    return matchSearch && matchTab;
  });

  const count = (key) => key === 'all' ? designs.length : designs.filter(d => normStatus(d) === key).length;

  return (
    <DesignerLayout>
      <div className="space-y-6 w-full">

        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Submissions</h1>
            <p className="text-gray-500 text-sm mt-0.5">Track the status of all your submitted templates.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchDesigns} className="gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
            <Button onClick={() => navigate('/designer/editor')} className="bg-violet-600 hover:bg-violet-700 gap-1.5">
              <Send className="w-4 h-4" /> New Template
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: 'Pending Review', value: count('pending'),   color: 'text-amber-600',   bg: 'bg-amber-100',   icon: Clock },
            { label: 'Approved',       value: count('approved'),  color: 'text-emerald-600', bg: 'bg-emerald-100', icon: CheckCircle },
            { label: 'Published',      value: count('published'), color: 'text-blue-600',    bg: 'bg-blue-100',    icon: Globe },
            { label: 'Rejected',       value: count('rejected'),  color: 'text-red-600',     bg: 'bg-red-100',     icon: XCircle },
          ].map(s => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="bg-white rounded-xl border border-gray-100 px-5 py-4 flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 ${s.color}`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold tabular-nums ${s.color}`}>{s.value}</p>
                  <p className="text-sm text-gray-500 font-medium">{s.label}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs + Search */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex bg-white border border-gray-200 rounded-xl p-1 gap-1">
            {FILTER_TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'bg-violet-600 text-white'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                <span className={`ml-1.5 text-xs ${activeTab === tab.key ? 'text-violet-200' : 'text-gray-400'}`}>
                  {count(tab.key)}
                </span>
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-white border-gray-200" />
          </div>
        </div>

        {/* Submission Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-7 h-7 animate-spin text-violet-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 flex flex-col items-center justify-center py-20 gap-3">
            <div className="w-12 h-12 bg-violet-100 rounded-2xl flex items-center justify-center">
              <Send className="w-6 h-6 text-violet-500" />
            </div>
            <p className="font-semibold text-gray-900">
              {search ? 'No submissions match your search' : 'No submissions yet'}
            </p>
            <p className="text-sm text-gray-400">Submit templates from My Templates for admin review.</p>
            <Button size="sm" className="bg-violet-600 hover:bg-violet-700 gap-1.5 mt-1" onClick={() => navigate('/designer/my-templates')}>
              Go to My Templates
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(d => {
              const status = normStatus(d);
              const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
              const StatusIcon = cfg.icon;
              const w = d.canvas_width || d.canvas_size?.width || d.canvas_size?.w || '—';
              const h = d.canvas_height || d.canvas_size?.height || d.canvas_size?.h || '—';
              const displayName = normName(d);

              return (
                <div
                  key={d.id}
                  className={`bg-white rounded-2xl border ${cfg.border} overflow-hidden hover:shadow-sm transition-shadow`}
                >
                  <div className={`${cfg.bg} px-5 py-4 flex items-center gap-4`}>
                    {/* Preview thumb */}
                    <div className="relative w-14 h-14 rounded-xl flex-shrink-0 overflow-hidden">
                      <TemplateThumbnail
                        template={_adaptSubmission(d)}
                        fallback={<LetterTile label={d.title || d.name || 'U'} textClassName="text-white text-xl font-bold select-none" />}
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900 truncate">{displayName}</p>
                        <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full flex-shrink-0 ${cfg.badge}`}>
                          <StatusIcon className="w-3 h-3" /> {cfg.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-400 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          Submitted {formatDate(d.submitted_at)}
                        </span>
                        {d.template_type && <span className="capitalize">{d.template_type}</span>}
                        <span className="tabular-nums">{w}×{h}px</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button variant="ghost" size="sm" className="gap-1.5 text-gray-600" onClick={() => setDetailDesign(d)}>
                        <Eye className="w-3.5 h-3.5" /> Details
                      </Button>
                      {status === 'rejected' && (
                        <Button
                          size="sm"
                          className="bg-violet-600 hover:bg-violet-700 gap-1.5"
                          onClick={() => handleResubmit(d)}
                          disabled={submitting}
                        >
                          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                          Resubmit
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Rejection feedback inline */}
                  {status === 'rejected' && (d.feedback || d.review_notes) && (
                    <div className="px-5 py-3 border-t border-red-100 flex items-start gap-2.5">
                      <MessageSquare className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-semibold text-red-700 mb-0.5">Admin Feedback</p>
                        <p className="text-sm text-red-600">{d.feedback || d.review_notes}</p>
                      </div>
                    </div>
                  )}
                  {status === 'approved' && (
                    <div className="px-5 py-3 border-t border-emerald-100 flex items-center gap-2.5">
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      <p className="text-xs text-emerald-700 font-medium">
                        Your template is now live in the iVisora template library.
                        {d.reviewed_at && ` Approved on ${formatDate(d.reviewed_at)}.`}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!detailDesign} onOpenChange={() => setDetailDesign(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-violet-500" />
              {normName(detailDesign) || 'Untitled'}
            </DialogTitle>
          </DialogHeader>
          {detailDesign && (() => {
            const dStatus = normStatus(detailDesign);
            const cfg = STATUS_CONFIG[dStatus] || STATUS_CONFIG.pending;
            const StatusIcon = cfg.icon;
            const w = detailDesign.canvas_width || detailDesign.canvas_size?.width || detailDesign.canvas_size?.w || '—';
            const h = detailDesign.canvas_height || detailDesign.canvas_size?.height || detailDesign.canvas_size?.h || '—';
            const feedback = detailDesign.feedback || detailDesign.review_notes;
            return (
              <div className="space-y-4 py-1">
                {/* Status */}
                <div className={`rounded-xl ${cfg.bg} border ${cfg.border} px-4 py-3 flex items-center gap-3`}>
                  <StatusIcon className={`w-5 h-5 flex-shrink-0 ${
                    dStatus === 'approved' ? 'text-emerald-500' :
                    dStatus === 'rejected' ? 'text-red-500' : 'text-amber-500'
                  }`} />
                  <div>
                    <p className="font-semibold text-sm text-gray-900">{cfg.label}</p>
                    {dStatus === 'pending' && (
                      <p className="text-xs text-gray-500">Waiting for admin review — usually within 24–48 hours</p>
                    )}
                    {dStatus === 'approved' && (
                      <p className="text-xs text-gray-500">Live in the iVisora template library</p>
                    )}
                    {dStatus === 'rejected' && (
                      <p className="text-xs text-gray-500">Please review the feedback and resubmit</p>
                    )}
                  </div>
                </div>

                {/* Meta */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gray-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-400 font-medium">Submitted</p>
                    <p className="text-sm font-semibold text-gray-800 mt-0.5">{formatDate(detailDesign.submitted_at)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl px-4 py-3">
                    <p className="text-xs text-gray-400 font-medium">Canvas Size</p>
                    <p className="text-sm font-semibold text-gray-800 mt-0.5 tabular-nums">{w}×{h}px</p>
                  </div>
                  {detailDesign.template_type && (
                    <div className="bg-gray-50 rounded-xl px-4 py-3">
                      <p className="text-xs text-gray-400 font-medium">Type</p>
                      <p className="text-sm font-semibold text-gray-800 mt-0.5 capitalize">{detailDesign.template_type}</p>
                    </div>
                  )}
                  {detailDesign.reviewed_at && (
                    <div className="bg-gray-50 rounded-xl px-4 py-3">
                      <p className="text-xs text-gray-400 font-medium">Reviewed</p>
                      <p className="text-sm font-semibold text-gray-800 mt-0.5">{formatDate(detailDesign.reviewed_at)}</p>
                    </div>
                  )}
                </div>

                {/* Feedback */}
                {feedback && (
                  <div className={`rounded-xl border px-4 py-3 ${
                    dStatus === 'rejected'
                      ? 'bg-red-50 border-red-200'
                      : 'bg-emerald-50 border-emerald-200'
                  }`}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <MessageSquare className={`w-4 h-4 ${
                        dStatus === 'rejected' ? 'text-red-500' : 'text-emerald-500'
                      }`} />
                      <p className={`text-xs font-semibold ${
                        dStatus === 'rejected' ? 'text-red-700' : 'text-emerald-700'
                      }`}>
                        Admin Feedback
                      </p>
                    </div>
                    <p className={`text-sm ${
                      dStatus === 'rejected' ? 'text-red-700' : 'text-emerald-700'
                    }`}>
                      {feedback}
                    </p>
                  </div>
                )}

                {/* Resubmit */}
                {dStatus === 'rejected' && (
                  <div className="flex gap-2 pt-1">
                    <Button
                      variant="outline"
                      className="flex-1 gap-1.5"
                      onClick={() => {
                        const td = detailDesign;
                        setDetailDesign(null);
                        const templateJson = typeof td.template_json === 'string'
                          ? (() => { try { return JSON.parse(td.template_json); } catch { return {}; } })()
                          : (td.template_json || {});
                        const pages = templateJson.pages;
                        if (!pages?.length) {
                          toast({
                            title: 'No design data',
                            description: 'This submission has no canvas content. Please re-submit from the editor.',
                            variant: 'destructive',
                          });
                          return;
                        }
                        navigate('/designer/editor', {
                          state: {
                            template: td,
                            canvasSize: { width: td.canvas_width || 1080, height: td.canvas_height || 1080 },
                            canvasBackground: pages[0]?.background || td.canvas_background || '#ffffff',
                            designerTemplateId: td.id,
                            designName: td.title || td.name || 'Untitled',
                          },
                        });
                      }}
                    >
                      <Eye className="w-4 h-4" /> Open in Editor
                    </Button>
                    <Button
                      className="flex-1 bg-violet-600 hover:bg-violet-700 gap-1.5"
                      onClick={() => { handleResubmit(detailDesign); }}
                      disabled={submitting}
                    >
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Resubmit
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </DesignerLayout>
  );
};

export default DesignerSubmissions;
